import React, { useEffect, useState } from 'react'
import { Box, Button, CircularProgress, Grid, TextField, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  AlertDialog,
  EditActions,
  formatDate,
  IconButtonWithTooltip,
  useGlobalMessageHandler
} from 'components'
import { type BaseUrl, type UsUrlRoleDescType } from 'services'
import { type HttpResponse } from 'interfaces'
import { resolveUrlValidationType, SOCIAL_URL_WARNING_TITLE, socialUrlUnderWebsiteWarning, validateUrl } from 'helpers/urls/urlValidation'
import { useSessionContext } from 'session'

export interface EditableUrlSlotProps<T extends BaseUrl> {
  descType: UsUrlRoleDescType
  url: T | null
  createEmptyUrl: () => T
  onSave: (url: T) => Promise<HttpResponse<T>>
  onDelete: (url: T) => Promise<boolean>
  onSaved: (url: T) => void
  onRemoved: (url: T) => void
  label?: string
}

export function EditableUrlSlot<T extends BaseUrl> ({
  descType,
  url,
  createEmptyUrl,
  onSave,
  onDelete,
  onSaved,
  onRemoved,
  label
}: EditableUrlSlotProps<T>): React.ReactElement {
  const [draft, setDraft] = useState<T | null>(url)
  const [disabled, setDisabled] = useState(true)
  const [validationError, setValidationError] = useState('')
  const [loading, setLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [socialWarning, setSocialWarning] = useState('')
  const { showSuccessMessage, showErrorMessage } = useGlobalMessageHandler()
  const { openNewTab } = useSessionContext()

  const typeString = resolveUrlValidationType(descType.desc, descType.type.desc.toLowerCase())

  useEffect(() => {
    setDraft(url)
    setDisabled(true)
    setValidationError('')
  }, [url])

  const handleAddClick = (): void => {
    setDraft(createEmptyUrl())
    setDisabled(false)
    setValidationError('')
  }

  const discardDraft = (): void => {
    setDraft(null)
    setDisabled(true)
    setValidationError('')
  }

  const handleValueChange = (value: string): void => {
    setDraft(prev => (prev == null ? prev : { ...prev, value }))
    const validation = validateUrl(value, typeString)
    setValidationError(validation.isValid ? '' : validation.message)
  }

  const handleCancel = (): void => {
    if (draft != null && draft.id > 0) {
      setDraft(url)
      setValidationError('')
      setDisabled(true)
    } else {
      discardDraft()
    }
  }

  const saveDraft = (toSave: T): void => {
    setLoading(true)
    void onSave(toSave)
      .then(response => {
        if (response.succeeded && response.result != null) {
          showSuccessMessage(`${descType.desc} saved.`)
          setDraft(response.result)
          setDisabled(true)
          onSaved(response.result)
        } else {
          showErrorMessage(`Failed to save ${descType.desc}.`)
        }
      })
      .catch(() => { showErrorMessage(`Failed to save ${descType.desc}.`) })
      .finally(() => { setLoading(false) })
  }

  const handleSave = (): void => {
    if (draft == null) return
    const validation = validateUrl(draft.value, typeString)
    if (!validation.isValid) {
      setValidationError(validation.message)
      return
    }
    const warning = socialUrlUnderWebsiteWarning(draft.value, descType.desc, descType.contentType)
    if (warning !== undefined) {
      setSocialWarning(warning)
      return
    }
    saveDraft(draft)
  }

  const handleDeleteClick = (): void => {
    if (draft == null) return
    if (draft.id <= 0) {
      discardDraft()
      return
    }
    setConfirmOpen(true)
  }

  const handleDeleteConfirmed = (): void => {
    setConfirmOpen(false)
    if (draft == null || draft.id <= 0) return
    const toDelete = draft
    setLoading(true)
    void onDelete(toDelete)
      .then(success => {
        if (success) {
          showSuccessMessage(`${descType.desc} deleted.`)
          onRemoved(toDelete)
        } else {
          showErrorMessage(`Failed to delete ${descType.desc}.`)
        }
      })
      .catch(() => { showErrorMessage(`Failed to delete ${descType.desc}.`) })
      .finally(() => { setLoading(false) })
  }

  if (draft == null) {
    return (
      <Button
        variant='outlined'
        startIcon={<AddIcon />}
        onClick={handleAddClick}
        fullWidth
      >
        Add {descType.desc}
      </Button>
    )
  }

  if (loading) {
    return <Grid item xs={12} display='flex' justifyContent='center'><CircularProgress /></Grid>
  }

  return (
    <>
      <Grid container alignItems='center' spacing={1} wrap='nowrap'>
        <Grid item xs='auto'>
          <Typography noWrap>{label ?? descType.desc}</Typography>
        </Grid>
        <Grid item xs>
          <TextField
            label={draft.value.length !== 0 ? `Last Updated: ${formatDate(draft.lastUpdated)}` : 'Enter URL'}
            variant='outlined'
            value={draft.value}
            disabled={disabled}
            onChange={(event) => { handleValueChange(event.target.value) }}
            error={validationError !== ''}
            helperText={validationError !== '' ? validationError : undefined}
            fullWidth
          />
        </Grid>
        <Grid item xs='auto'>
          <Box sx={{ whiteSpace: 'nowrap' }}>
            <EditActions
              entityName={descType.desc}
              editing={!disabled}
              showCancel={draft.id > 0}
              onEdit={() => { setDisabled(false) }}
              onCancel={handleCancel}
              onSave={handleSave}
            />
            <IconButtonWithTooltip
              label={draft.id <= 0 ? `Cancel ${descType.desc}` : `Delete ${descType.desc}`}
              color='secondary'
              onClick={handleDeleteClick}
              icon={<DeleteIcon />}
            />
            <IconButtonWithTooltip
              label={`Open ${descType.desc}`}
              color='secondary'
              onClick={() => { openNewTab(draft.value, false) }}
              icon={<OpenInNewIcon />}
              disabled={draft.value === '' || validationError !== ''}
            />
          </Box>
        </Grid>
      </Grid>
      <AlertDialog
        title='Delete Confirmation'
        contentText={`Are you sure you want to delete this ${descType.desc}? This action cannot be undone.`}
        nameButtonCancel='Cancel'
        nameButtonConfirm='Delete'
        onButtonClick={handleDeleteConfirmed}
        onClose={() => { setConfirmOpen(false) }}
        open={confirmOpen}
        containedButton
      />
      <AlertDialog
        title={SOCIAL_URL_WARNING_TITLE}
        contentText={socialWarning}
        nameButtonCancel='Cancel'
        nameButtonConfirm='Save anyway'
        onButtonClick={() => { if (draft != null) saveDraft(draft) }}
        onClose={() => { setSocialWarning('') }}
        open={socialWarning !== ''}
        containedButton
      />
    </>
  )
}
